/**
 * InboxDrawer — Unified Inbox Packet 1 + Packet 2
 *
 * Kind-specific detail drawer for InboxItem rows.
 * Reuses the WorkItemDrawer slide-over pattern from Control Tower.
 *
 * Packet 1 AC-inbox-9:
 *   QC row      → shows AI Self-QC criteria
 *   Decision row → shows impact / method / rationale
 *   Cascade row  → shows subject type + cascade summary
 *
 * Packet 2 AC-inbox-10/11/12/13/20:
 *   QC row      → Approve + Request Changes (with reason dialog, RR-0004 WIP error handling)
 *   Decision row → Approve + Reject (with reason dialog)
 *   Cascade row  → Approve + Reject + Request Ad-hoc Review
 *   Decided rows → read-only (no action controls)
 *   Permission gating: buttons hidden when user lacks the required permission
 */

import React, { useState } from 'react';
import { X, Sparkles, CheckCircle, XCircle, GitBranch, Inbox, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui/Toast';
import { t, __resolve } from '@/lib/i18n';
// AC-hard-3: shared ReasonDialog — single source of truth for reason dialogs
import { ReasonDialog } from '@/components/roros/ReasonDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import {
  useRorosInboxQcTransition,
  useRorosInboxDecisionApprove,
  useRorosInboxDecisionReject,
  useRorosInboxCascadeApprove,
  useRorosInboxCascadeReject,
  useRorosInboxCascadeRequestAdHoc,
} from '@/hooks/roros/inbox';
import type { InboxItemDTO } from '@/types/roros/inbox';
// F1: use the canonical types from the shared client-facing types bundle.
// The real persisted field is `name` (not `criterion`) — see QcCriterion in
// src/types/roros/control-tower.ts which mirrors server/modules/ai-operators/ai-operators.types.ts.
import type { QcSelfReport, QcCriterion } from '@/types/roros/control-tower';

// ─── State helpers (AC-inbox-13, F2) ──────────────────────────────────────
//
// F2: hasActionableState replaces isDecided for button-hiding logic.
// Keeps the drawer consistent with InboxPage's split logic.

/** Returns true when the item has actionable state (buttons should be shown). */
function hasActionableState(item: InboxItemDTO): boolean {
  if (item.kind === 'qc') return item.state === 'quality_confirmation';
  if (item.kind === 'decision') return item.state === 'pending_approval';
  if (item.kind === 'cascade') return item.state === 'pending';
  return false;
}

// ─── RR-0004 WIP-limit error helper ─────────────────────────────────────────

function formatTransitionErrorMessage(err: any, fallback: string): string {
  const code: string = err?.code ?? '';
  const details = err?.details ?? {};
  const message: string = err?.message ?? '';

  if (code === 'ROROS_WIP_LIMIT_REACHED') {
    const limit = typeof details?.limit === 'number' ? details.limit : null;
    const limitType: string = details?.limitType ?? '';
    if (limitType === 'per_actor' && limit !== null) {
      return __resolve(
        'ui::Cannot submit: this actor already has {{limit}} in-progress items. Resolve some before assigning more.',
        { limit },
      );
    }
    if (limit !== null) {
      return __resolve(
        'ui::Cannot submit: WIP limit reached ({{limit}} in-progress). Resolve some items before assigning more.',
        { limit },
      );
    }
    return t('Cannot submit: WIP limit reached. Resolve some in-progress items before assigning more.');
  }

  if (code === 'ROROS_INVALID_STATUS_TRANSITION') {
    return t('This status transition is not allowed from the current state.');
  }

  // F4: friendly QC-approve errors
  if (code === 'ROROS_QC_REQUIRED') {
    return t('Cannot approve: the AI Self-QC report has not passed. Review the QC criteria and try again.');
  }
  if (code === 'ROROS_PARENT_CHILDREN_INCOMPLETE') {
    const n = typeof details?.activeChildren === 'number' ? details.activeChildren : null;
    if (n !== null) return __resolve('ui::Cannot approve: {{n}} sub-item(s) still active. Complete them first.', { n });
    return t('Cannot approve: some sub-items are still active. Complete them first.');
  }

  return message || fallback;
}

// ─── QC Panel (AC-inbox-10) ─────────────────────────────────────────────────

/** F2: parse a value that may arrive as a JSON string or already-parsed object. */
function parseJsonish(value: unknown): unknown {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try { return JSON.parse(value); } catch { return null; }
  }
  return null;
}

function QcPanel({ item, onActionDone }: { item: InboxItemDTO; onActionDone: () => void }) {
  // F2: qc_self_report may be a JSON string from Postgres; parse defensively.
  const report = parseJsonish(item.qcSelfReport) as QcSelfReport | null;
  // F2: guard against missing/malformed criteria array — never call .filter() on non-array.
  const criteria: QcCriterion[] = Array.isArray(report?.criteria) ? report!.criteria : [];

  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.ROROS_CONTROL_TOWER_MANAGE);
  const decided = !hasActionableState(item);

  const qcTransition = useRorosInboxQcTransition();
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [requestChangesOpen, setRequestChangesOpen] = useState(false);

  const handleApprove = () => {
    qcTransition.mutate(
      { sourceId: item.sourceId, toStatus: 'released', reason: t('Approved from Inbox') },
      {
        onSuccess: () => {
          showToast.success(t('Approved — work item released.'));
          onActionDone();
        },
        onError: (err: any) => {
          // F4: use friendly error formatter for ROROS_QC_REQUIRED / ROROS_PARENT_CHILDREN_INCOMPLETE
          showToast.error(formatTransitionErrorMessage(err, t('Failed to approve work item.')));
        },
      },
    );
  };

  // RR-0004: mutateAsync so ReasonDialog can await and own its close lifecycle.
  const handleRequestChanges = async (reason: string) => {
    try {
      await qcTransition.mutateAsync({ sourceId: item.sourceId, toStatus: 'in_progress', reason });
      showToast.success(t('Changes requested — sent back with your feedback.'));
      onActionDone();
    } catch (err: any) {
      const friendlyMessage = formatTransitionErrorMessage(err, t('Failed to request changes.'));
      showToast.error(friendlyMessage);
      const friendlyError = new Error(friendlyMessage);
      (friendlyError as any).cause = err;
      throw friendlyError;
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-muted-foreground mb-1">{t('State')}</p>
        <Badge variant={item.state === 'quality_confirmation' ? 'warning' : 'default'}>
          {item.state}
        </Badge>
      </div>
      {item.deliverableText && (
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-1">{t('Deliverable')}</p>
          <p className="text-sm whitespace-pre-wrap rounded-md border border-border bg-muted/20 p-3">
            {item.deliverableText}
          </p>
        </div>
      )}
      {report ? (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles className="h-4 w-4 text-primary-600" aria-hidden />
            <p className="text-sm font-medium">{t('AI Self-QC Report')}</p>
            <span className="ml-auto">
              <Badge variant={report.finalPassed ? 'success' : 'error'}>
                {criteria.filter(c => c.passed).length}/{criteria.length} {t('passed')}
              </Badge>
            </span>
          </div>
          <ul className="space-y-1.5" data-testid="qc-criteria-list">
            {criteria.map((c, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                {c.passed
                  ? <CheckCircle className="h-4 w-4 mt-0.5 text-success-600 flex-shrink-0" aria-hidden />
                  : <XCircle className="h-4 w-4 mt-0.5 text-error-600 flex-shrink-0" aria-hidden />
                }
                {/* F1: field is `name` (not `criterion`) — see QcCriterion in ai-operators.types.ts */}
                <span>{c.name}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{t('No QC report available.')}</p>
      )}

      {/* AC-inbox-10/13/20: actions — hidden when decided OR permission missing */}
      {!decided && canManage && (
        <div className="flex gap-2 pt-3 border-t border-border" data-testid="qc-action-buttons">
          <Button
            size="sm"
            variant="success"
            onClick={() => setApproveConfirmOpen(true)}
            disabled={qcTransition.isPending}
            isLoading={qcTransition.isPending}
            data-testid="drawer-approve-qc-btn"
          >
            <CheckCircle className="h-4 w-4 mr-1" aria-hidden />
            {t('Approve')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRequestChangesOpen(true)}
            disabled={qcTransition.isPending}
            data-testid="drawer-request-changes-btn"
          >
            <RefreshCw className="h-4 w-4 mr-1" aria-hidden />
            {t('Request Changes')}
          </Button>
        </div>
      )}

      <ConfirmDialog
        isOpen={approveConfirmOpen}
        onClose={() => setApproveConfirmOpen(false)}
        title={t('Approve work item?')}
        description={`${t('This will mark')} "${item.title}" ${t('as released.')}`}
        confirmText={t('Approve')}
        onConfirm={() => {
          setApproveConfirmOpen(false);
          handleApprove();
        }}
      />

      <ReasonDialog
        isOpen={requestChangesOpen}
        title={t('Request Changes')}
        onClose={() => setRequestChangesOpen(false)}
        onSubmit={handleRequestChanges}
        isPending={qcTransition.isPending}
      />
    </div>
  );
}

// ─── Decision Panel (AC-inbox-11) ──────────────────────────────────────────

function DecisionPanel({ item, onActionDone }: { item: InboxItemDTO; onActionDone: () => void }) {
  const { hasPermission } = usePermissions();
  const canApprove = hasPermission(PERMISSIONS.ROROS_DECISIONS_APPROVE);
  const decided = !hasActionableState(item);

  const approveMutation = useRorosInboxDecisionApprove();
  const rejectMutation = useRorosInboxDecisionReject();
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const handleApprove = () => {
    approveMutation.mutate(
      { sourceId: item.sourceId },
      {
        onSuccess: () => {
          showToast.success(t('Decision approved.'));
          onActionDone();
        },
        onError: (err: any) => {
          showToast.error(err?.message ?? t('Failed to approve decision.'));
        },
      },
    );
  };

  const handleReject = async (reason: string) => {
    try {
      await rejectMutation.mutateAsync({ sourceId: item.sourceId, reason });
      showToast.success(t('Decision rejected.'));
      onActionDone();
    } catch (err: any) {
      const friendlyMessage = formatTransitionErrorMessage(err, t('Failed to reject decision.'));
      showToast.error(friendlyMessage);
      const friendlyError = new Error(friendlyMessage);
      (friendlyError as any).cause = err;
      throw friendlyError;
    }
  };

  const impactVariant: Record<string, 'error' | 'warning' | 'secondary' | 'default'> = {
    root: 'error',
    trunk: 'warning',
    branch: 'secondary',
    leaf: 'default',
  };

  return (
    <div className="space-y-4" data-testid="decision-drawer-detail">
      {item.impactClassification && (
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-1">{t('Impact')}</p>
          <Badge variant={impactVariant[item.impactClassification] ?? 'default'}>{item.impactClassification}</Badge>
        </div>
      )}
      {item.methodUsed && (
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-1">{t('Method')}</p>
          <p className="text-sm">{item.methodUsed}</p>
        </div>
      )}
      {item.rationale && (
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-1">{t('Rationale')}</p>
          <p className="text-sm whitespace-pre-wrap rounded-md border border-border bg-muted/20 p-3">
            {item.rationale}
          </p>
        </div>
      )}
      {item.moderatorRationale && (
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles className="h-4 w-4 text-primary-600" aria-hidden />
            <p className="text-sm font-medium">{t('AI Moderator Rationale')}</p>
          </div>
          <p className="text-sm whitespace-pre-wrap rounded-md border border-border bg-muted/20 p-3">
            {item.moderatorRationale}
          </p>
        </div>
      )}

      {/* AC-inbox-11/13/20: actions — hidden when decided OR permission missing */}
      {!decided && canApprove && (
        <div className="flex gap-2 pt-3 border-t border-border" data-testid="decision-action-buttons">
          <Button
            size="sm"
            variant="success"
            onClick={() => setApproveConfirmOpen(true)}
            disabled={approveMutation.isPending || rejectMutation.isPending}
            isLoading={approveMutation.isPending}
            data-testid="drawer-approve-decision-btn"
          >
            <CheckCircle className="h-4 w-4 mr-1" aria-hidden />
            {t('Approve')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setRejectOpen(true)}
            disabled={approveMutation.isPending || rejectMutation.isPending}
            data-testid="drawer-reject-decision-btn"
          >
            <XCircle className="h-4 w-4 mr-1" aria-hidden />
            {t('Reject')}
          </Button>
        </div>
      )}

      <ConfirmDialog
        isOpen={approveConfirmOpen}
        onClose={() => setApproveConfirmOpen(false)}
        title={t('Approve decision?')}
        description={`${t('This will approve')} "${item.title}".`}
        confirmText={t('Approve')}
        onConfirm={() => {
          setApproveConfirmOpen(false);
          handleApprove();
        }}
      />

      <ReasonDialog
        isOpen={rejectOpen}
        title={t('Reject Decision')}
        onClose={() => setRejectOpen(false)}
        onSubmit={handleReject}
        isPending={rejectMutation.isPending}
      />
    </div>
  );
}

// ─── Cascade Panel (AC-inbox-12) ──────────────────────────────────────────

function CascadePanel({ item, onActionDone }: { item: InboxItemDTO; onActionDone: () => void }) {
  const { hasPermission } = usePermissions();
  const canDecide = hasPermission(PERMISSIONS.ROROS_VERIFICATION_INBOX_DECIDE);
  const decided = !hasActionableState(item);

  const approveMutation = useRorosInboxCascadeApprove();
  const rejectMutation = useRorosInboxCascadeReject();
  const adHocMutation = useRorosInboxCascadeRequestAdHoc();
  const anyPending = approveMutation.isPending || rejectMutation.isPending || adHocMutation.isPending;

  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const handleApprove = () => {
    approveMutation.mutate(
      { sourceId: item.sourceId },
      {
        onSuccess: () => {
          showToast.success(t('Approved successfully.'));
          onActionDone();
        },
        onError: (err: any) => {
          showToast.error(err?.message ?? t('Action failed.'));
        },
      },
    );
  };

  const handleReject = async (reason: string) => {
    try {
      await rejectMutation.mutateAsync({ sourceId: item.sourceId, reason });
      showToast.success(t('Rejected successfully.'));
      onActionDone();
    } catch (err: any) {
      const friendlyMessage = formatTransitionErrorMessage(err, t('Failed to reject.'));
      showToast.error(friendlyMessage);
      const friendlyError = new Error(friendlyMessage);
      (friendlyError as any).cause = err;
      throw friendlyError;
    }
  };

  const handleAdHoc = () => {
    adHocMutation.mutate(
      { sourceId: item.sourceId },
      {
        onSuccess: () => {
          showToast.success(t('Ad-hoc review requested.'));
          onActionDone();
        },
        onError: (err: any) => {
          showToast.error(err?.message ?? t('Action failed.'));
        },
      },
    );
  };

  return (
    <div className="space-y-4" data-testid="cascade-drawer-detail">
      {item.subjectType && (
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-1">{t('Subject Type')}</p>
          <p className="text-sm capitalize">{item.subjectType.replace(/_/g, ' ')}</p>
        </div>
      )}
      {item.cascadeSummary && (
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-1">{t('Summary')}</p>
          <p className="text-sm whitespace-pre-wrap rounded-md border border-border bg-muted/20 p-3">
            {item.cascadeSummary}
          </p>
        </div>
      )}
      <div>
        <p className="text-sm font-medium text-muted-foreground mb-1">{t('State')}</p>
        <Badge variant={item.state === 'pending' ? 'warning' : 'default'}>{item.state}</Badge>
      </div>
      {/* F1: show rejection reason when item is rejected and reason is available */}
      {item.state === 'rejected' && item.decisionReason && (
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-1">{t('Rejection reason')}</p>
          <p className="text-sm whitespace-pre-wrap rounded-md border border-border bg-muted/20 p-3">
            {item.decisionReason}
          </p>
        </div>
      )}

      {/* AC-inbox-12/13/20: actions — hidden when decided OR permission missing */}
      {!decided && canDecide && (
        <div className="flex flex-wrap gap-2 pt-3 border-t border-border" data-testid="cascade-action-buttons">
          <Button
            size="sm"
            variant="success"
            onClick={() => setApproveConfirmOpen(true)}
            disabled={anyPending}
            isLoading={approveMutation.isPending}
            data-testid="drawer-approve-cascade-btn"
          >
            <CheckCircle className="h-4 w-4 mr-1" aria-hidden />
            {t('Approve')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleAdHoc}
            disabled={anyPending}
            isLoading={adHocMutation.isPending}
            data-testid="drawer-adhoc-cascade-btn"
          >
            {t('Request Ad-hoc Review')}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setRejectOpen(true)}
            disabled={anyPending}
            data-testid="drawer-reject-cascade-btn"
          >
            <XCircle className="h-4 w-4 mr-1" aria-hidden />
            {t('Reject')}
          </Button>
        </div>
      )}

      <ConfirmDialog
        isOpen={approveConfirmOpen}
        onClose={() => setApproveConfirmOpen(false)}
        title={t('Approve cascade submission?')}
        description={`${t('This will approve')} "${item.title}".`}
        confirmText={t('Approve')}
        onConfirm={() => {
          setApproveConfirmOpen(false);
          handleApprove();
        }}
      />

      <ReasonDialog
        isOpen={rejectOpen}
        title={t('Reject Cascade Submission')}
        onClose={() => setRejectOpen(false)}
        onSubmit={handleReject}
        isPending={rejectMutation.isPending}
      />
    </div>
  );
}

// ─── Main Drawer ───────────────────────────────────────────────────────────

interface InboxDrawerProps {
  item: InboxItemDTO | null;
  onClose: () => void;
  /** Called after a successful action so the parent can refresh its state if needed. */
  onActionDone?: () => void;
}

// Wrapped in a function so labels resolve via t() at render time (translatable).
function kindDrawerLabel(kind: string): string {
  const map: Record<string, string> = {
    qc: t('QC Review'),
    decision: t('Decision'),
    cascade: t('Cascade'),
  };
  return map[kind] ?? kind;
}

const KIND_ICONS: Record<string, React.ReactNode> = {
  qc: <Sparkles className="h-4 w-4 text-primary-500" aria-hidden />,
  decision: <GitBranch className="h-4 w-4 text-blue-500" aria-hidden />,
  cascade: <Inbox className="h-4 w-4 text-orange-500" aria-hidden />,
};

export function InboxDrawer({ item, onClose, onActionDone }: InboxDrawerProps) {
  if (!item) return null;

  const handleActionDone = () => {
    onActionDone?.();
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-label={t('Inbox item details')}
      aria-modal="true"
      data-testid="inbox-drawer"
      className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-background shadow-xl border-l border-border overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3 shrink-0">
        <div className="flex items-center gap-2">
          {KIND_ICONS[item.kind]}
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {kindDrawerLabel(item.kind)}
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('Close drawer')}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Body — C1: scrollbar-hiding triple required for single-scroll-container pattern */}
      <div className="flex-1 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden p-4 space-y-4">
        <h2 className="text-base font-semibold leading-tight">{item.title}</h2>

        {item.kind === 'qc' && <QcPanel item={item} onActionDone={handleActionDone} />}
        {item.kind === 'decision' && <DecisionPanel item={item} onActionDone={handleActionDone} />}
        {item.kind === 'cascade' && <CascadePanel item={item} onActionDone={handleActionDone} />}
      </div>
    </div>
  );
}
